import Check from "@mui/icons-material/Check";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  AssociateFacilitiesDialogFacilitiesQuery,
  AssociateFacilitiesDialogFacilitiesQueryVariables,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";

export const FACILITIES_QUERY = gql(`
  query AssociateFacilitiesDialogFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        customerFacilityId
        name
        streetAddress1
        streetAddress2
        city
        state
        county
        zip
        tags {
          id
          name
        }
        tenant {
          name
        }
      }
      count
    }
  }
`);

export type FacilityRow = AssociateFacilitiesDialogFacilitiesQuery["facilities"]["items"][number];

type AssociateFacilitiesDialogProps = {
  open: boolean;
  onClose: () => void;
  existingFacilityIds?: string[];
  onSave: (facilityIds: string[]) => void;
  subtitle: string;
  allFacilities?: boolean;
  showTenantColumn?: boolean;
  excludeSecondaryWasteFacilities?: boolean;
};

export const AssociateFacilitiesDialog = ({
  open,
  onClose,
  existingFacilityIds,
  onSave,
  subtitle,
  allFacilities = false,
  showTenantColumn = false,
  excludeSecondaryWasteFacilities = false,
}: AssociateFacilitiesDialogProps) => {
  const theme = useTheme();
  const { tenantId } = useTenant();
  const { hasPermissions } = useAuthorization();
  const { user } = useCurrentUser();

  // if the user has WriteAllFacility it can view assign to anything, otherwise we filter to what they have access to
  const search = useMemo(() => {
    let baseSearch = `tenantId:${tenantId}${
      hasPermissions([Permission.WriteAllFacility])
        ? ""
        : ` id:${user?.facilities.map((f) => f.id).join("|")}`
    }`;

    if (excludeSecondaryWasteFacilities) {
      baseSearch += " isPrimaryWasteFacility:true";
      baseSearch += " wasteFeatureFlagOverride:!DISABLED";
    }

    return baseSearch;
  }, [hasPermissions, tenantId, user?.facilities, excludeSecondaryWasteFacilities]);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedFacilityIds(existingFacilityIds ?? []);
    }
  }, [existingFacilityIds, open]);

  const anyAssociationsModified = !isEqual(existingFacilityIds ?? [], selectedFacilityIds);

  const handleClose = useCallback(() => {
    setSelectedFacilityIds([]);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!anyAssociationsModified) {
      handleClose();
      return;
    }

    onSave(selectedFacilityIds);
    handleClose();
  }, [selectedFacilityIds, handleClose, onSave, anyAssociationsModified]);

  const columns = useMemo(() => {
    const baseColumns: OmnisearchGridColDef<FacilityRow>[] = [
      {
        field: "name",
        headerName: "Name",
        filterKeyType: "facility",
        flex: 1,
      },
      {
        field: "streetAddress",
        flex: 1,
        headerName: "Address",
        width: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.streetAddress1) return "";
          const { streetAddress1, city, state, zip } = row;
          return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
        },
      },
    ];

    if (!showTenantColumn) {
      return baseColumns;
    }

    return [
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 1,
        valueGetter: (_: any, row: FacilityRow) => row.tenant?.name ?? "",
      },
      ...baseColumns,
    ];
  }, [showTenantColumn]);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Facility Info",
        fields: ["name", "customerFacilityId"],
      },
      {
        name: "Location",
        fields: ["state", "county", "city", "zip", "streetAddress1"],
      },
      {
        name: "Tags",
        fields: ["tags"],
      },
    ],
    []
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="associate-facilities-dialog-title"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="associate-facilities-dialog-title">Associate Facilities</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={theme.spacing(1)}>
          <Typography variant="body1">{subtitle}</Typography>
          <OmnisearchDataGrid<
            AssociateFacilitiesDialogFacilitiesQuery,
            AssociateFacilitiesDialogFacilitiesQueryVariables,
            FacilityRow
          >
            persistenceKey="associate-facilities-dialog"
            withPadding={false}
            gridSx={{
              minHeight: "300px",
              "& .MuiDataGrid-virtualScroller": {
                minHeight: "200px",
                maxHeight: "380px",
                overflowY: "auto",
              },
            }}
            onSelectedIdsChanged={(rows) => setSelectedFacilityIds(rows as string[])}
            selectedIds={selectedFacilityIds}
            dataQuery={FACILITIES_QUERY}
            columns={columns}
            defaultSearch={allFacilities ? "" : search}
            getItems={(data) => data.facilities.items}
            getCount={(data) => data.facilities.count}
            noDataMessage="No facilities available."
            initialPageSize={10}
            isURLDriven={false}
            showManageColumns={false}
            filterGroups={filterGroups}
            excludeFilterColumns={["streetAddress"]}
            additionalFilterColumns={[
              { header: "Facility ID", key: "customerFacilityId" },
              { header: "State/Territory", key: "state" },
              { header: "County", key: "county" },
              { header: "Street Address", key: "streetAddress1" },
              { header: "City", key: "city" },
              { header: "Zip", key: "zip" },
              { header: "Tags", key: "tags" },
            ]}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pr: theme.spacing(2), pb: theme.spacing(2) }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" startIcon={<Check />} onClick={handleSave}>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};
